import {AbstractO2dElement, customElement, property, html, generateShortId} from '@o2d/shared';
import {nothing} from 'lit';
import {ifDefined} from 'lit/directives/if-defined.js';
import {map} from 'lit/directives/map.js';
import type {ValueChangedDetail} from '../interfaces/event-detail.types.js';
import {VALUE_CHANGED_EVENT} from '../interfaces/event-detail.types.js';

/**
 * Auto-resizing textarea with value/value-changed pattern (ADR-010).
 */
@customElement('o2d-textarea')
export class O2dTextareaElement extends AbstractO2dElement {

    private _textareaId = `o2d-textarea-${generateShortId()}`;

    @property({attribute: false})
    value: string = '';

    @property()
    label: string | null = null;

    @property()
    placeholder: string | null = null;

    @property({type: Boolean})
    readonly: boolean = false;

    @property({type: Number, attribute: 'maxlength'})
    maxlength: number | null = null;

    @property({attribute: false})
    errors: {[key: string]: string[]} = {};

    private get _currentLength(): number {
        return this.value.length;
    }

    protected _onInput(evt: Event): void {
        if (this.readonly) return;
        const str: string = (evt.target as HTMLTextAreaElement).value;
        if (str !== this.value) {
            this.value = str;
            this.fireEvent<ValueChangedDetail>(VALUE_CHANGED_EVENT, {value: str});
        }
    }

    protected override render(): unknown {
        const flatErrors = Object.values(this.errors).flat();

        return html`
            ${this.label !== null
                ? html`<label for="${this._textareaId}">${this.label}</label>`
                : nothing}
            <textarea
                id="${this._textareaId}"
                .value="${this.value}"
                placeholder="${ifDefined(this.placeholder ?? undefined)}"
                ?readonly="${this.readonly}"
                maxlength="${ifDefined(this.maxlength ?? undefined)}"
                @input="${this._onInput}"
            ></textarea>
            ${this.maxlength !== null
                ? html`<span class="char-counter">${this._currentLength} / ${this.maxlength}</span>`
                : nothing}
            ${flatErrors.length > 0
                ? html`<ul class="input-error">
                    ${map(flatErrors, error => html`<li>${error}</li>`)}
                </ul>`
                : nothing}
        `;
    }
}
